import { registerMockAPI } from '~/mocks/mockApis';

import { useMutation } from '@tanstack/react-query';

import { axiosInstance } from '~/services';

import { API_ENDPOINT } from '~/constants';

import type {
  CollectingResultReportDetail,
  GetCollectingResultReportDetailParams,
  GetCollectingResultReportDetailResponse,
} from '~/types/delivery-agency.types';

const fetchCollectingResultReportDetail = async (
  params: GetCollectingResultReportDetailParams
): Promise<CollectingResultReportDetail> => {
  const { data } = await axiosInstance.get<GetCollectingResultReportDetailResponse>(
    `${API_ENDPOINT.DELIVERY_AGENCY.GET_COLLECTING_RESULT_REPORT_DETAIL}/${params.reportId}`
  );
  return data.data;
};

export const useGetCollectingResultReportDetailMutation = (options?: { showGlobalDimOverlay?: boolean }) => {
  return useMutation({
    mutationFn: fetchCollectingResultReportDetail,
    meta: { showGlobalDimOverlay: options?.showGlobalDimOverlay ?? true },
  });
};

registerMockAPI<GetCollectingResultReportDetailResponse>({
  method: 'get',
  endpoint: `${API_ENDPOINT.DELIVERY_AGENCY.GET_COLLECTING_RESULT_REPORT_DETAIL}/:id([A-Z]{0,}\\d[A-Z0-9]{0,})`,
  label: '회수 결과 리포트/상세',
  scenarios: [
    {
      name: '회수 완료 (사진 있음)',
      response: {
        data: {
          collectingRequestCode: 'RR20260804001',
          shippingLabelNumber: 'CL20260804001',
          collectingShippingLabelStatus: { code: 'COLLECT_COMPLETED', description: '회수 완료' },
          courierCode: { code: 'NM', description: '넥스트마일' },
          collectingCompletedImageUrl: 'https://picsum.photos/seed/collecting/480/640',
        },
      },
    },
    {
      name: '회수 완료 (사진 없음)',
      response: {
        data: {
          collectingRequestCode: 'RR20260804002',
          shippingLabelNumber: 'CL20260804002',
          collectingShippingLabelStatus: { code: 'COLLECT_COMPLETED', description: '회수 완료' },
          courierCode: { code: 'NM', description: '넥스트마일' },
          collectingCompletedImageUrl: null,
        },
      },
    },
    {
      name: '사진 URL 깨짐 (로드 실패)',
      response: {
        data: {
          collectingRequestCode: 'RR20260804003',
          shippingLabelNumber: 'CL20260804003',
          collectingShippingLabelStatus: { code: 'COLLECT_COMPLETED', description: '회수 완료' },
          courierCode: { code: 'NM', description: '넥스트마일' },
          collectingCompletedImageUrl: 'https://kls.kurly.com/not-found-image.jpg',
        },
      },
    },
    {
      name: '회수 출발 준비 (긴 운송장번호·사진 없음)',
      response: {
        data: {
          collectingRequestCode: 'RR20260804005',
          shippingLabelNumber: '250-A0-179142213QKAO-0001',
          collectingShippingLabelStatus: { code: 'COLLECT_DEPARTURE_READY', description: '회수 출발 준비' },
          courierCode: { code: 'NM', description: '넥스트마일' },
          collectingCompletedImageUrl: null,
        },
      },
    },
    {
      name: '회수 예정 (운송장 미부여)',
      response: {
        data: {
          collectingRequestCode: 'RR20260804004',
          shippingLabelNumber: null,
          collectingShippingLabelStatus: { code: 'COLLECT_ACCEPTED', description: '회수 예정' },
          courierCode: { code: 'NM', description: '넥스트마일' },
          collectingCompletedImageUrl: null,
        },
      },
    },
  ],
});
